import { LiaTransparentElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { setDeveloper } from '../api/developerSession.js';
import { redeemImpersonation } from '../api/developer.js';
import { navigate } from '../../router.js';

/**
 * Where a borrowed view lands, and the only place a code becomes a session.
 *
 * <b>The code arrives in the address bar; the session never does.</b> That is the whole shape of this
 * page: it takes the one thing that is safe to put in a URL - single-use, worthless within a minute, not
 * a credential - posts it to the public API, and receives the session in a response body. The alternative
 * the portal used to rely on, writing the session into shared storage, stopped being possible when the
 * two applications were deployed apart, and putting the session in the URL instead would have left a
 * working credential in history, in a referrer and in every proxy log on the way.
 *
 * The code is spent before anything is rendered, and the address is replaced immediately afterwards, so a
 * reload does not try again with something already used - it just shows the dashboard.
 */
export class UserImpersonate extends LiaTransparentElement
{
   static properties = { refusal: { state: true } };

   constructor()
   {
      super();
      this.refusal = '';
   }

   connectedCallback()
   {
      super.connectedCallback();
      void this.redeem();
   }

   async redeem()
   {
      const code = new URLSearchParams(location.search).get('code');

      if (!code)
      {
         this.refusal = 'This link carries no code.';
         return;
      }

      try
      {
         const borrowed = await redeemImpersonation(code);

         setDeveloper({
            token: borrowed.session,
            principalId: borrowed.principalId,
            email: borrowed.email,
            expiresAt: borrowed.expiresAt,

            // What the banner names. The operator is not in this response by name - the borrowed session
            // carries it - so this says what is true here: the view is borrowed.
            impersonatedBy: 'an operator'
         });

         navigate('/', { replace: true });
      }
      catch (error)
      {
         this.refusal = error.message;
      }
   }

   render()
   {
      return html`
         <lia-auth-layout brand="covdbg" heading="Opening a borrowed view" .showThemeToggle=${false}>
            ${this.refusal
               ? html`<lia-alert
                    variant="danger"
                    heading="That link cannot be opened"
                    message=${`${this.refusal} An impersonation link works once, and only for a minute after `
                       + 'it is created. Ask for a new one.'}
                 ></lia-alert>`
               : nothing}
         </lia-auth-layout>`;
   }
}

customElements.define('user-impersonate', UserImpersonate);
